import React, { useState, useMemo, useCallback } from 'react';
import { View, Pressable, Platform, LayoutAnimation, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

// How "long" a user message has to be before we offer to collapse it.
// Tuned roughly against the ChatGPT-style threshold — short prompts stay
// fully visible, multi-paragraph prompts get collapsed by default.
const DEFAULT_CHAR_THRESHOLD = 500;
const DEFAULT_LINE_THRESHOLD = 8;
// Collapsed clip height (~9-10 lines at the bubble line-height). Picked to
// match ChatGPT's preview height so the fade lands a line or two from the end.
const DEFAULT_COLLAPSED_MAX_HEIGHT = 220;
const FADE_HEIGHT = 56;

const computeShouldCollapse = (content, charThreshold, lineThreshold) => {
  if (typeof content !== 'string' || !content) return false;
  if (content.length > charThreshold) return true;
  const lineCount = (content.match(/\n/g) || []).length + 1;
  return lineCount > lineThreshold;
};

function MessageExpandable({
  content,
  children,
  fadeColor,
  align = 'left',
  charThreshold = DEFAULT_CHAR_THRESHOLD,
  lineThreshold = DEFAULT_LINE_THRESHOLD,
  collapsedMaxHeight = DEFAULT_COLLAPSED_MAX_HEIGHT,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldOffer = useMemo(
    () => computeShouldCollapse(content, charThreshold, lineThreshold),
    [content, charThreshold, lineThreshold]
  );

  const toggle = useCallback(() => {
    if (Platform.OS !== 'web') {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
    setIsExpanded(prev => !prev);
  }, []);

  if (!shouldOffer) {
    return children;
  }

  const effectiveFadeColor = fadeColor || colors.solid_color_primary;

  const clipStyle = isExpanded
    ? styles.clipExpanded
    : [styles.clipCollapsed, { maxHeight: collapsedMaxHeight }];

  const toggleAlignStyle = align === 'right'
    ? styles.toggleAlignRight
    : styles.toggleAlignLeft;

  return (
    <View style={styles.root}>
      <View style={clipStyle}>
        {children}
        {!isExpanded && (
          <LinearGradient
            colors={['rgba(0,0,0,0)', effectiveFadeColor]}
            style={[styles.fade, { height: FADE_HEIGHT }]}
            pointerEvents="none"
          />
        )}
      </View>

      <Pressable
        onPress={toggle}
        style={({ pressed, hovered }) => [
          styles.toggle,
          toggleAlignStyle,
          (pressed || hovered) && styles.toggleActive,
        ]}
        accessibilityRole="button"
        accessibilityState={{ expanded: isExpanded }}
        accessibilityLabel={isExpanded ? t('messages.showLess') : t('messages.showMore')}
      >
        <Text size="s" weight="medium" color="secondary" style={styles.toggleText}>
          {isExpanded ? t('messages.showLess') : t('messages.showMore')}
        </Text>
        <Icon
          name={isExpanded ? 'chevron-up' : 'chevron-down'}
          size={11}
          color={colors.text_color_secondary}
        />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  // Intentionally no width: we let the parent (bubble / content slot) drive
  // the wrapper's width. Forcing width:100% would over-stretch the
  // `display: inline-block` user bubble in MessageWide and reflow the layout
  // of short-but-long-enough-to-collapse messages.
  root: {},
  clipCollapsed: {
    overflow: 'hidden',
    position: 'relative',
  },
  clipExpanded: {
    position: 'relative',
  },
  fade: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
  },
  toggle: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
    paddingVertical: 2,
    paddingHorizontal: 2,
    borderRadius: 4,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'opacity 0.15s ease',
    }),
  },
  toggleActive: {
    opacity: 0.7,
  },
  toggleAlignLeft: {
    alignSelf: 'flex-start',
  },
  toggleAlignRight: {
    alignSelf: 'flex-end',
  },
  toggleText: {
    marginRight: 4,
  },
});

MessageExpandable.propTypes = {
  content: PropTypes.string,
  children: PropTypes.node.isRequired,
  fadeColor: PropTypes.string,
  align: PropTypes.oneOf(['left', 'right']),
  charThreshold: PropTypes.number,
  lineThreshold: PropTypes.number,
  collapsedMaxHeight: PropTypes.number,
};

MessageExpandable.displayName = 'MessageExpandable';

export default React.memo(MessageExpandable);
